import React, { useState } from 'react';
import { Plus, FolderKanban, Calendar, Clock, DollarSign, User, Search, Edit, Flag, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import api from '@/utils/api';
import useApiData from '@/hooks/useApiData';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/utils/helpers';
import ProjectEditModal from '@/components/ProjectEditModal';
import ProjectTasksModal from '@/components/ProjectTasksModal';
import ProjetoCreateModal from '@/components/ProjetoCreateModal';
import GlobalTaskCreateModal from '@/components/GlobalTaskCreateModal';
import { useAuth } from '@/context/AuthContext';
import { Checkbox } from '@/components/ui/checkbox';


const STATUS_COLUMNS = [
  { id: 'backlog', title: 'Backlog', color: 'bg-slate-500', textColor: 'text-slate-700', bg: 'bg-slate-100/80' },
  { id: 'em_andamento', title: 'Em Andamento', color: 'bg-blue-500', textColor: 'text-blue-800', bg: 'bg-blue-50/80' },
  { id: 'revisao', title: 'Revisão', color: 'bg-yellow-500', textColor: 'text-yellow-800', bg: 'bg-yellow-50/80' },
  { id: 'entregue', title: 'Entregue', color: 'bg-green-500', textColor: 'text-green-800', bg: 'bg-green-50/80' },
];

const STATUS_CONFIG = {
  backlog: { label: 'Backlog', color: 'bg-slate-100 text-slate-700' },
  em_andamento: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700' },
  revisao: { label: 'Revisão', color: 'bg-yellow-100 text-yellow-700' },
  entregue: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};

const PRIORITY_CONFIG = {
  'baixa': { label: 'Baixa', color: 'bg-slate-100 text-slate-600', icon: 'text-slate-400' },
  'media': { label: 'Média', color: 'bg-blue-100 text-blue-600', icon: 'text-blue-500' },
  'alta': { label: 'Alta', color: 'bg-orange-100 text-orange-600', icon: 'text-orange-500' },
  'urgente': { label: 'Urgente', color: 'bg-red-100 text-red-600', icon: 'text-red-500' }
};

const PROJECT_TYPES = ['website', 'social_media', 'branding', 'marketing_digital', 'consultoria', 'outro'];

export default function Projetos() {
  const { user } = useAuth();
  const { data: projectsData, refresh: refreshProjects } = useApiData('/projects');
  const { data: clientsData } = useApiData('/clients');
  const { data: usersData } = useApiData('/admin/users');
  const projects = projectsData || [];
  const clients = clientsData || [];
  const users = usersData || [];

  console.log('[Projetos] Current User:', user?.id, user?.name);
  console.log('[Projetos] Users in list:', users.length);

  const [isDialogOpen, setIsDialogOpen] = useState(false); // remove later if not needed
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isGlobalTaskModalOpen, setIsGlobalTaskModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('kanban');
  const [draggedProject, setDraggedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTasksModalOpen, setIsTasksModalOpen] = useState(false);
  const [tasksProject, setTasksProject] = useState(null);

  const [formData, setFormData] = useState({
    client_id: '',
    name: '',
    description: '',
    project_type: 'outro',
    priority: 'media',
    start_date: '',
    deadline: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null
      };
      await api.post('/projects', payload);
      toast.success('Projeto criado com sucesso!');
      setIsDialogOpen(false);
      resetForm();
      refreshProjects();

    } catch (error) {
      toast.error('Erro ao criar projeto');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      name: '',
      description: '',
      project_type: 'outro',
      priority: 'media',
      start_date: '',
      deadline: ''
    });
  };

  const handleDragStart = (e, project) => {
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggedProject || draggedProject.status === newStatus) return;

    try {
      await api.put(`/projects/${draggedProject.id}`, { status: newStatus });
      toast.success('Status atualizado!');
      refreshProjects();

    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
    setDraggedProject(null);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'N/A';
  };

  const getProgressPercent = (project) => {
    const tasks = project.tasks || [];
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(t => t.status === 'concluida').length;
    return Math.min(100, (completedTasks / tasks.length) * 100);
  };
  
  const handleEditProject = (project) => {
    setEditingProject(project);
    setIsEditModalOpen(true);
  };

  const handleOpenTasks = (project) => {
    setTasksProject(project);
    setIsTasksModalOpen(true);
  };

  const getUserContext = (usersList) => {
     // Aqui na listagem não temos acesso imediato à lista de users para o Kanban, 
     // deixaremos apenas um placeholder com a inicial ou U a menos que possamos pegar.
     return 'U';
  };

  const handleToggleUserTask = async (projectObj, taskId, checked) => {
    try {
      const updatedTasks = (projectObj.tasks || []).map(t => 
        t.id === taskId ? { ...t, status: checked ? 'concluida' : 'pendente' } : t
      );
      
      const payload = { ...projectObj, tasks: updatedTasks };
      
      // Sanitizar envio assim como a listagem 
      payload.tasks = payload.tasks.map(t => ({
        ...t,
        assignee_id: t.assignee_id === 'none' ? null : t.assignee_id
      }));

      await api.put(`/projects/${projectObj.id}`, payload);
      refreshProjects();
    } catch (error) {
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getClientName(p.client_id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getProjectsByStatus = (status) => filteredProjects.filter(p => p.status === status);

  // Métricas Operacionais
  const totalProjects = projects.length;
  const projectsAtivos = projects.filter(p => !['entregue', 'cancelado'].includes(p.status)).length;
  const projectsAtrasados = projects.filter(p => p.deadline && new Date(p.deadline) < new Date() && !['entregue', 'cancelado'].includes(p.status)).length;
  const totalTasks = projects.reduce((acc, p) => acc + (p.tasks || []).length, 0);
  const totalCompletedTasks = projects.reduce((acc, p) => acc + (p.tasks || []).filter(t => t.status === 'concluida').length, 0);

  return (
    <div className="space-y-6" data-testid="projetos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Projetos</h1>
          <p className="text-slate-600 mt-1">Gestão operacional de projetos</p>
        </div>
        <Button className="bg-primary hover:bg-primary/90" onClick={() => setIsCreateModalOpen(true)} data-testid="new-project-btn">
          <Plus className="h-4 w-4 mr-2" />
          Novo Projeto
        </Button>
      </div>

      {/* Métricas Operacionais */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-xs text-slate-500 uppercase">Total de Projetos</p>
          <p className="text-2xl font-bold text-slate-900">{totalProjects}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-xs text-slate-500 uppercase">Projetos Ativos</p>
          <p className="text-2xl font-bold text-blue-600">{projectsAtivos}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-xs text-slate-500 uppercase">Projetos Atrasados</p>
          <p className="text-2xl font-bold text-red-500">{projectsAtrasados}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-xs text-slate-500 uppercase">Total de Tarefas</p>
          <p className="text-2xl font-bold text-primary">{totalTasks}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-xs text-slate-500 uppercase">Tarefas Concluídas</p>
          <p className={`text-2xl font-bold text-green-600`}>{totalCompletedTasks}</p>
        </div>
      </div>

      {/* Filtros e Visualização */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            placeholder="Buscar projetos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList>
            <TabsTrigger value="kanban">Kanban</TabsTrigger>
            <TabsTrigger value="lista">Lista</TabsTrigger>
            <TabsTrigger value="minhastarefas">Minhas Tarefas</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Kanban View - Visual original + funções do Trello */}
      {viewMode === 'kanban' && (
        <div className="flex gap-4 overflow-x-auto pb-4" style={{minHeight: '60vh'}}>
          {STATUS_COLUMNS.map((column) => {
            const columnProjects = getProjectsByStatus(column.id);
            return (
              <div
                key={column.id}
                className="flex-shrink-0 w-80 bg-slate-50 rounded-xl flex flex-col"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                {/* Column Header */}
                <div className="flex items-center justify-between p-4 pb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${column.color}`}></div>
                    <h3 className="font-semibold text-slate-900">{column.title}</h3>
                  </div>
                  <Badge variant="secondary">{columnProjects.length}</Badge>
                </div>

                {/* Cards */}
                <div className="flex-1 px-3 flex flex-col gap-3 overflow-y-auto" style={{maxHeight: 'calc(100vh - 320px)'}}>
                  {columnProjects.map((project) => {
                    const hasOverdueTasks = project.tasks && project.tasks.some(
                      t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'concluida'
                    );
                    const memberIds = [...new Set((project.tasks || []).map(t => t.assignee_id).filter(Boolean))];
                    const taskTotal = (project.tasks || []).length;
                    const taskDone = (project.tasks || []).filter(t => t.status === 'concluida').length;

                    return (
                      <div
                        key={project.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, project)}
                        onClick={() => handleOpenTasks(project)}
                        className="bg-white rounded-lg p-4 shadow-sm border border-slate-100 cursor-pointer hover:shadow-md hover:border-primary/20 transition-all group"
                        data-testid={`project-card-${project.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-slate-900 flex-1 leading-snug">{project.name}</h4>
                          <div className="flex items-center gap-1 ml-1 flex-shrink-0">
                            {project.priority && (
                              <Badge className={PRIORITY_CONFIG[project.priority]?.color} variant="secondary">
                                {PRIORITY_CONFIG[project.priority]?.label}
                              </Badge>
                            )}
                            <button
                              className="opacity-0 group-hover:opacity-100 h-6 w-6 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-all"
                              onClick={(e) => { e.stopPropagation(); handleEditProject(project); }}
                              data-testid={`edit-project-${project.id}`}
                            >
                              <Edit className="h-3 w-3" />
                            </button>
                          </div>
                        </div>

                        <p className="text-sm text-slate-500 mb-2">{getClientName(project.client_id)}</p>

                        {project.deadline && (
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                            <Calendar className="h-3 w-3" />
                            <span>Prazo: {formatDate(project.deadline)}</span>
                          </div>
                        )}

                        {hasOverdueTasks && (
                          <div className="flex items-center gap-1 text-[10px] text-red-600 font-semibold mb-2 bg-red-50 px-1.5 py-0.5 rounded w-fit">
                            <AlertCircle className="h-3 w-3" /> Tarefas atrasadas
                          </div>
                        )}

                        {taskTotal > 0 && (
                          <div className="mb-2">
                            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                              <span>{taskDone} / {taskTotal} tarefas</span>
                              <span>{getProgressPercent(project).toFixed(0)}%</span>
                            </div>
                            <Progress value={getProgressPercent(project)} className="h-1.5" />
                          </div>
                        )}

                        {memberIds.length > 0 && (
                          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                            <span className="text-slate-500 text-xs">Membros ({memberIds.length})</span>
                            <div className="flex -space-x-2">
                              {memberIds.slice(0, 3).map((_, idx) => (
                                <div key={idx} className="w-6 h-6 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[10px] font-bold text-slate-600">
                                  U
                                </div>
                              ))}
                              {memberIds.length > 3 && (
                                <div className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-slate-500">
                                  +{memberIds.length - 3}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {columnProjects.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      Nenhum projeto
                    </div>
                  )}
                </div>

                {/* + Adicionar um cartão (Trello function) */}
                <div className="p-3 pt-2">
                  <button
                    onClick={() => setIsDialogOpen(true)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Adicionar um cartão
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lista View */}

      {viewMode === 'lista' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50/50 border-b border-slate-100">
                <tr>
                  <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Projeto</th>
                  <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                  <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Prioridade</th>
                  <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Prazo</th>
                  <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Progresso</th>
                  <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Tarefas</th>
                  <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => (
                  <tr key={project.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="p-4 font-medium">{project.name}</td>
                    <td className="p-4 text-sm text-slate-600">{getClientName(project.client_id)}</td>
                    <td className="p-4">
                      <Badge className={STATUS_CONFIG[project.status]?.color}>
                        {STATUS_CONFIG[project.status]?.label}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Badge className={PRIORITY_CONFIG[project.priority]?.color} variant="secondary">
                        {PRIORITY_CONFIG[project.priority]?.label}
                      </Badge>
                    </td>
                    <td className="p-4 text-sm">{project.deadline ? formatDate(project.deadline) : '-'}</td>
                    <td className="p-4 w-32">
                      <div className="flex items-center gap-2">
                        <Progress value={getProgressPercent(project)} className="h-2 flex-1" />
                        <span className="text-xs text-slate-500">{getProgressPercent(project).toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">
                      {(project.tasks || []).filter(t => t.status === 'concluida').length} / {(project.tasks || []).length}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleOpenTasks(project)}
                        >
                          Tarefas
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEditProject(project)}
                          data-testid={`edit-project-list-${project.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" /> Editar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredProjects.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      Nenhum projeto encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Visão Minhas Tarefas */}
      {viewMode === 'minhastarefas' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Minhas Tarefas</h3>
            <Button variant="outline" className="text-primary border-primary hover:bg-primary/10" onClick={() => setIsGlobalTaskModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Adicionar
            </Button>
          </div>
          <div className="space-y-4">
            {projects.flatMap(p => 
              (p.tasks || []).filter(t => String(t.assignee_id) === String(user?.id)).map(t => ({...t, projectName: p.name, projectId: p.id, project: p}))
            ).length === 0 ? (
              <p className="text-slate-500 text-sm">Você não possui tarefas atribuídas.</p>
            ) : (
              projects.flatMap(p => 
                (p.tasks || []).filter(t => String(t.assignee_id) === String(user?.id)).map(t => ({...t, projectName: p.name, projectId: p.id, project: p}))
              ).sort((a,b) => {
                if (a.status === 'concluida' && b.status !== 'concluida') return 1;
                if (a.status !== 'concluida' && b.status === 'concluida') return -1;
                if(!a.due_date) return 1;
                if(!b.due_date) return -1;
                return new Date(a.due_date) - new Date(b.due_date);
              }).map(task => {
                const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'concluida';
                const isChecked = task.status === 'concluida';
                return (
                 <div key={`${task.projectId}-${task.id}`} className={`group flex flex-col md:flex-row items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors gap-4 ${isChecked ? 'opacity-60 grayscale' : ''}`}>
                  <div className="flex-1 flex gap-3 w-full">
                    <Checkbox 
                      checked={isChecked}
                      onCheckedChange={(c) => handleToggleUserTask(task.project, task.id, c)}
                      className={`mt-1.5 w-5 h-5 rounded-full transition-colors ${isChecked ? 'bg-green-500 border-green-500' : 'border-slate-300 hover:border-primary'}`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-sm tracking-tight flex-1 font-medium ${isChecked ? 'line-through text-slate-500' : 'text-slate-800'}`}>
                          {task.title}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs">
                        <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 font-medium">
                          <FolderKanban className="h-3 w-3" /> {task.projectName}
                        </span>
                        
                        <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${PRIORITY_CONFIG[task.priority]?.color}`}>
                           <Flag className={`h-3 w-3 ${PRIORITY_CONFIG[task.priority]?.icon}`} />
                           {PRIORITY_CONFIG[task.priority]?.label}
                        </div>

                        {task.due_date && (
                          <span className={`flex items-center font-medium gap-1 px-1.5 py-0.5 rounded ${isOverdue && !isChecked ? 'text-red-600 bg-red-50' : 'text-slate-500'}`}>
                            <Calendar className="h-3 w-3" />
                            {new Date(task.due_date).toLocaleDateString('pt-BR')}  {isOverdue && !isChecked && '(Atrasada)'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 w-full md:w-auto mt-2 md:mt-0 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => handleOpenTasks(task.project)}>
                      Ver no Projeto
                    </Button>
                  </div>
                </div>
              )})
            )}
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      <ProjectEditModal 
        project={editingProject}
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingProject(null); }}
        onSuccess={refreshProjects}
        clients={clients}
      />

      {tasksProject && (
        <ProjectTasksModal
          project={tasksProject}
          isOpen={isTasksModalOpen}
          onClose={() => setIsTasksModalOpen(false)}
          onSuccess={refreshProjects}
        />
      )}

      {isCreateModalOpen && (
        <ProjetoCreateModal
          clients={clients}
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={refreshProjects}
        />
      )}

      {isGlobalTaskModalOpen && (
        <GlobalTaskCreateModal
          projects={projects}
          users={users}
          isOpen={isGlobalTaskModalOpen}
          onClose={() => setIsGlobalTaskModalOpen(false)}
          onSuccess={refreshProjects}
        />
      )}

    </div>
  );
}
